// animOverlays.js
// ============================================================
// Performer interaction overlays for animation cues.
// Clickable HTML circles anchored to SVG elements for
// armed (click-to-start), OSC toggle, and visual feedback.
// Used by the rotate, scale, o2p and trans cue handlers.
//
// Also provides shared geometry helpers for screen-space
// positioning of SVG elements.
// ============================================================

import { screenSpaceDelta } from "../util/svgCoords.js";
import { mayOperate } from "./controlAccess.js";
import { onReset } from "../system/teardown.js";

window._oscillaHitLabels = window._oscillaHitLabels || [];

// ============================================================
// OSC TOGGLE STATE PERSISTENCE
// Store oscMode + oscColor per uid so it survives page reload
// ============================================================
const OSC_STATE_KEY = "oscilla.oscToggleState";
const TOGGLE_STATES_KEY = "oscilla.toggleStates";

function getOscStateMap() {
    try {
        const raw = localStorage.getItem(OSC_STATE_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
}

export function saveOscState(uid, oscMode, oscColor) {
    const map = getOscStateMap();
    if (oscMode) {
        map[uid] = { oscMode, oscColor };
    } else {
        delete map[uid];  // Remove when toggled off
    }
    localStorage.setItem(OSC_STATE_KEY, JSON.stringify(map));
}

export function loadOscState(uid) {
    const map = getOscStateMap();
    return map[uid] || null;
}

// ============================================================
// TOGGLE STATES PERSISTENCE (decoupled from OSC)
// Store stateIndex + color per uid so it survives page reload
// ============================================================
function getToggleStatesMap() {
    try {
        const raw = localStorage.getItem(TOGGLE_STATES_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
}

export function saveToggleState(uid, stateIndex, color) {
    const map = getToggleStatesMap();
    if (stateIndex > 0) {
        map[uid] = { stateIndex, color };
    } else {
        delete map[uid];  // Remove when back to state 0 (idle)
    }
    localStorage.setItem(TOGGLE_STATES_KEY, JSON.stringify(map));
}

export function loadToggleState(uid) {
    const map = getToggleStatesMap();
    return map[uid] || null;
}

// ============================================================
// CLIENT COLOR CHANGE LISTENER
// Update OSC toggle overlays when local client changes their color
// ============================================================
window.addEventListener("oscilla:client-color-changed", (e) => {
    const newColor = e.detail?.color;
    if (!newColor) return;

    const labels = window._oscillaHitLabels || [];
    for (const rec of labels) {
        // Only update overlays that THIS client toggled on
        if (rec.oscMode && rec.toggledByLocal) {
            rec.oscColor = newColor;
            saveOscState(rec.uid, rec.oscMode, newColor);
            updateHitCircleColor(rec);
        }
    }

    // Also update selector visuals (fill color uses client color when active)
    updateAllSelectorVisuals();
});

// ============================================================
// TOGGLE STATE SYNC LISTENER
// Update toggle state visuals when another client changes state
// ============================================================
window.addEventListener("oscilla:toggle-state-sync", (e) => {
    const { uid, stateIndex, color } = e.detail || {};
    if (!uid) return;

    const labels = window._oscillaHitLabels || [];
    const rec = labels.find(r => r.uid === uid);
    if (!rec || !rec.toggleStates) return;

    // Update record state (from remote client)
    rec.toggleStateIndex = stateIndex ?? 0;
    rec.toggleColor = color || null;
    rec.toggledByLocal = false;  // This came from another client

    // Persist to localStorage
    saveToggleState(uid, stateIndex, color);

    // Update visual
    updateHitCircleColor(rec);
});

// ============================================================
// INDEPENDENT HIT-LABEL REPOSITION LOOP
// Runs at reduced frequency (10fps) for scroll/viewport sync.
// Animation loops call repositionAllHitLabels() directly when needed.
// ============================================================
let _hitLabelTimer = null;
const HIT_LABEL_IDLE_INTERVAL = 100;  // 10fps when idle

function _hitLabelRepositionLoop() {
    if (!window._oscillaHitLabels || window._oscillaHitLabels.length === 0) {
        _hitLabelTimer = null;
        return;
    }
    repositionAllHitLabels();
    // Also update polygon selectors in the same loop for smooth tracking
    repositionAllO2PPathSelectors();
    // Use setTimeout instead of RAF for lower idle frequency
    _hitLabelTimer = setTimeout(_hitLabelRepositionLoop, HIT_LABEL_IDLE_INTERVAL);
}

function _ensureHitLabelLoop() {
    if (_hitLabelTimer == null && window._oscillaHitLabels?.length > 0) {
        _hitLabelTimer = setTimeout(_hitLabelRepositionLoop, HIT_LABEL_IDLE_INTERVAL);
    }
}

function _stopHitLabelLoop() {
    if (_hitLabelTimer != null) {
        clearTimeout(_hitLabelTimer);
        _hitLabelTimer = null;
    }
}

// ============================================================
// TOUCH/DRAG MODE SUPPORT
// Track active drag sessions for o2p touch mode

export function shouldCreateHitLabel(cfg) {
    if (cfg._armed) return true;
    if (cfg.init === "ghost") return false;
    // Opt-in when any interactive/OSC feature is explicitly enabled
    if (cfg.osc || cfg.oscToggle || cfg.toggleStates) return true;
    return false;
}

// ============================================================
// GROUP SELECTOR SYSTEM
// Click to activate a group and show its control overlays; any number of
// groups can be active at once.
// ============================================================

window._activeO2PGroups = window._activeO2PGroups || new Set();

const SELECTOR_SIZE = 15;       // Visible dot size
const SELECTOR_HIT_SIZE = 40;   // Touch target size
const SELECTOR_STROKE_FALLBACK = "#cc3333";  // Fallback stroke if path has no color
const SELECTOR_ACTIVE_STROKE = "#22cc44";  // Green stroke (selected)
const SELECTOR_HIDE_DELAY = 3000;  // Hide selectors after 3 seconds of no interaction

let selectorHideTimer = null;
let selectorsPinned  = false;   // DSL editor open — selectors stay up, no auto-hide

/** Get client color with 0.8 opacity as hex+alpha */
function getClientColorWithAlpha() {
    const color = window.localClientColor || localStorage.getItem("clientColor") || "#ffaa00";
    // Convert hex to rgba with 0.8 opacity (cc = 204 in decimal = ~0.8 of 255)
    return color + "cc";
}

/**
 * Show all o2p path selectors with auto-hide timer
 */
export function showO2PSelectors() {
    for (const sel of Object.values(window._o2pPathSelectors || {})) {
        sel.dot.style.opacity = "0.8";
        sel.hit.style.pointerEvents = "auto";
    }
    // Reset hide timer. Pinned means the DSL editor is open, and the
    // selectors stay up for as long as it is.
    clearTimeout(selectorHideTimer);
    if (!selectorsPinned) {
        selectorHideTimer = setTimeout(hideO2PSelectors, SELECTOR_HIDE_DELAY);
    }
}

/**
 * Hide ALL o2p path selectors. The user re-taps the score to bring
 * them back. Selection state is tracked separately (via colour /
 * opacity on the polygon body), so no need to keep claimed ones
 * permanently visible.
 */
export function hideO2PSelectors() {
    if (selectorsPinned) return;   // DSL editor open — ignore transient hides
    clearTimeout(selectorHideTimer);
    for (const sel of Object.values(window._o2pPathSelectors || {})) {
        sel.dot.style.opacity = "0";
        sel.hit.style.pointerEvents = "none";
    }
}

/**
 * Pin the o2p path selectors visible while the DSL editor is open.
 *
 * Choosing a path is authoring, not performing, so the selectors follow the
 * authoring context instead of a gesture — double-tap on the score is
 * play / pause. Selectors registered later are revealed as they appear: the
 * polling block below picks up rotate hits for up to 30 s after load.
 */
export function setO2PSelectorsPinned(on) {
    on = !!on;
    if (selectorsPinned === on) return;
    selectorsPinned = on;
    if (on) {
        showO2PSelectors();
    } else {
        hideO2PSelectors();   // pin already cleared, so this goes through
    }
}

/**
 * Get the stroke color from an SVG path element
 * Falls back to fill color if no stroke, then to fallback color
 */
function getPathColor(pathEl) {
    if (!pathEl) return SELECTOR_STROKE_FALLBACK;

    // Try computed style first
    const computed = window.getComputedStyle(pathEl);
    let color = computed.stroke;

    // If stroke is none/transparent, try fill
    if (!color || color === "none" || color === "transparent" || color === "rgba(0, 0, 0, 0)") {
        color = computed.fill;
    }

    // If still no color, try attributes directly
    if (!color || color === "none" || color === "transparent" || color === "rgba(0, 0, 0, 0)") {
        color = pathEl.getAttribute("stroke") || pathEl.getAttribute("fill");
    }

    // Return fallback if no color found
    if (!color || color === "none" || color === "transparent") {
        return SELECTOR_STROKE_FALLBACK;
    }

    return color;
}

/**
 * Toggle a group's active state (multi-select supported)
 */
function toggleGroupActive(groupId) {
    const wasActive = window._activeO2PGroups.has(groupId);

    if (wasActive) {
        // Deactivate this group
        window._activeO2PGroups.delete(groupId);
    } else {
        // Activate this group (others stay active)
        window._activeO2PGroups.add(groupId);
    }

    // Update all selector visuals
    updateAllSelectorVisuals();

    // Update all overlay visibility
    repositionAllHitLabels();

    // Dispatch event for extension system
    const eventDetail = {
        selected: Array.from(window._activeO2PGroups),
        selectedSet: window._activeO2PGroups,
        groupId: groupId,
        wasDeactivation: wasActive,
        wasActivation: !wasActive
    };
    window.dispatchEvent(new CustomEvent('oscilla:group-selected', { detail: eventDetail }));
}

/**
 * Update visual state of all selectors (active/inactive)
 */
function updateAllSelectorVisuals() {
    const clientFill = getClientColorWithAlpha();

    // Update polygon selectors - use path color when inactive, green when active
    for (const [, sel] of Object.entries(window._o2pPathSelectors)) {
        const isActive = window._activeO2PGroups.has(sel.groupId);
        sel.dot.style.borderColor = isActive ? SELECTOR_ACTIVE_STROKE : (sel.pathColor || SELECTOR_STROKE_FALLBACK);
        sel.dot.style.background = isActive ? clientFill : "transparent";
        sel.dot.style.transform = isActive ? "scale(1.2)" : "scale(1)";
        sel.dot.style.opacity = isActive ? "1" : "0.8";
    }
}

/**
 * Check if an overlay should be visible based on group activation
 * @returns {boolean} true if visible
 */
function shouldShowOverlayByGroup(rec) {
    // Score-level override: any role / extension can set this to bypass
    // selection-based gating (e.g. polygonfield's conductor/overview roles
    // need to see and click all overlays regardless of which polygon is
    // currently "selected").
    if (window._showAllOverlays) return true;

    // If no selectors exist at all, show everything — UNLESS the score
    // explicitly opts into selector-gated visibility (window._scoreUsesPolygonSelectors).
    // In that case selectors are coming via the polling loop; "show all"
    // during that window would briefly leak every polygon's overlays on
    // load, so we hide instead.
    const hasO2PPathSelectors = Object.keys(window._o2pPathSelectors).length > 0;
    if (!hasO2PPathSelectors) {
        if (window._scoreUsesPolygonSelectors) return false;
        return true;
    }

    // Get config from element
    const cfg = rec.groupEl?._oscillaCfg;
    const group = cfg?.group;
    const pathName = cfg?.pathName || cfg?.path;  // Path for path-following o2ps

    // Check if this is a rotate overlay (uid starts with rot_)
    const isRotateOverlay = rec.uid && rec.uid.startsWith("rot_");
    if (isRotateOverlay) {
        // Derive polygon name from uid (rot_triangleA → triangleA)
        const polygonName = rec.uid.replace(/^rot_/, "");
        // Show only if this polygon is in the active set
        return window._activeO2PGroups.has(polygonName);
    }

    // Elements without a group AND without a polygon path are always visible
    if (!group && !pathName) return true;

    // If no groups are active, hide overlays that have a group or path
    if (window._activeO2PGroups.size === 0) return false;

    // Show if this overlay's group matches any active group
    if (group && window._activeO2PGroups.has(group)) return true;

    // Show if this overlay's path matches any active group (for path-following o2ps)
    if (pathName && window._activeO2PGroups.has(pathName)) return true;

    return false;
}

// ============================================================
// POLYGON SELECTORS
// Selectors positioned on polygon rotate overlays that activate
// the corresponding control group (rot_triangleA → triangleA)
// ============================================================

window._o2pPathSelectors = window._o2pPathSelectors || {};

/**
 * Create a polygon selector from a rotate overlay uid
 * @param {string} uid - Rotate overlay uid like "rot_triangleA"
 * @param {HTMLElement} rotateOverlay - The rotate overlay element (hit label div)
 */
export function createO2PPathSelector(uid, rotateOverlay) {
    if (!uid || !uid.startsWith("rot_")) return;
    if (window._o2pPathSelectors[uid]) return;  // Already exists

    // Derive control group name from uid (rot_triangleA → triangleA)
    const groupId = uid.replace(/^rot_/, "");

    // Get the path element and its color
    const pathEl = document.getElementById(groupId);
    const pathColor = getPathColor(pathEl);

    // Create visible ring selector (starts hidden)
    const dot = document.createElement("div");
    dot.className = "oscilla-polygon-selector";
    dot.dataset.uid = uid;
    dot.dataset.groupId = groupId;
    dot.style.cssText = `
        position: fixed;
        width: ${SELECTOR_SIZE}px;
        height: ${SELECTOR_SIZE}px;
        border-radius: 50%;
        background: transparent;
        border: 2px solid ${pathColor};
        pointer-events: none;
        z-index: 9001;
        opacity: 0;
        transition: background 0.15s, transform 0.15s, border-color 0.15s, opacity 0.2s;
    `;

    // Create larger invisible hit area (starts disabled)
    const hit = document.createElement("div");
    hit.className = "oscilla-polygon-selector-hit";
    hit.dataset.uid = uid;
    hit.dataset.groupId = groupId;
    hit.style.cssText = `
        position: fixed;
        width: ${SELECTOR_HIT_SIZE}px;
        height: ${SELECTOR_HIT_SIZE}px;
        border-radius: 50%;
        background: transparent;
        cursor: pointer;
        z-index: 9002;
        margin-left: -${SELECTOR_HIT_SIZE / 2}px;
        margin-top: -${SELECTOR_HIT_SIZE / 2}px;
        pointer-events: none;
    `;

    hit.addEventListener("click", (e) => {
        e.stopPropagation();
        toggleGroupActive(groupId);
        hideO2PSelectors();  // Hide immediately after selection
    });

    document.body.appendChild(dot);
    document.body.appendChild(hit);

    window._o2pPathSelectors[uid] = { dot, hit, uid, groupId, rotateOverlay, pathColor };

    // Born while the DSL editor is open — reveal it now rather than waiting
    // for the next showO2PSelectors() call.
    if (selectorsPinned) {
        dot.style.opacity = "0.8";
        hit.style.pointerEvents = "auto";
    }

    // Initial position update
    updateO2PPathSelectorPosition(uid);
}

/**
 * Update position of a polygon selector based on its polygon path
 * Positions at 22% along the path to avoid clashing with other objects
 */
function updateO2PPathSelectorPosition(uid) {
    const sel = window._o2pPathSelectors[uid];
    if (!sel) return;

    // Get the path element (uid is rot_triangleA, path is triangleA)
    const pathId = uid.replace(/^rot_/, "");
    const pathEl = document.getElementById(pathId);

    if (!pathEl || typeof pathEl.getTotalLength !== "function") {
        // Fallback to rotate overlay position
        const rec = window._oscillaHitLabels?.find(r => r.uid === uid);
        if (!rec || !rec.groupEl) return;
        const box = rec.groupEl.getBoundingClientRect();
        const x = box.right + 8;
        const y = box.top - 8;
        sel.dot.style.left = `${x - SELECTOR_SIZE / 2}px`;
        sel.dot.style.top = `${y - SELECTOR_SIZE / 2}px`;
        sel.hit.style.left = `${x}px`;
        sel.hit.style.top = `${y}px`;
    } else {
        // Position at 22% along the path
        const totalLength = pathEl.getTotalLength();
        const point = pathEl.getPointAtLength(totalLength * 0.22);

        // Convert SVG coords to screen coords
        const svg = pathEl.ownerSVGElement;
        if (svg) {
            const pt = svg.createSVGPoint();
            pt.x = point.x;
            pt.y = point.y;
            const ctm = pathEl.getScreenCTM();
            if (ctm) {
                const screenPt = pt.matrixTransform(ctm);
                const { dx, dy } = screenSpaceDelta(svg);
                const x = screenPt.x + dx;
                const y = screenPt.y + dy;
                sel.dot.style.left = `${x - SELECTOR_SIZE / 2}px`;
                sel.dot.style.top = `${y - SELECTOR_SIZE / 2}px`;
                sel.hit.style.left = `${x}px`;
                sel.hit.style.top = `${y}px`;
            }
        }
    }

    // Visual state (background, border, transform) is set by event-driven
    // paths — updateAllSelectorVisuals on selection events and the
    // extension-side remote-claim paint. Doing it here every 100 ms would
    // overwrite remote-claim colours within a frame of being painted.
}

/**
 * Update all polygon selector positions
 */
export function repositionAllO2PPathSelectors() {
    for (const uid of Object.keys(window._o2pPathSelectors)) {
        updateO2PPathSelectorPosition(uid);
    }
}

/**
 * Scan for rotate overlays and create polygon selectors
 * Call this after rotate cues are initialized
 */
export function initO2PPathSelectors() {
    const labels = window._oscillaHitLabels || [];
    const rotateLabels = labels.filter(rec => rec.uid && rec.uid.startsWith("rot_"));

    for (const rec of rotateLabels) {
        createO2PPathSelector(rec.uid, rec.div);
    }
}

// Re-position selectors on scroll/resize
window.addEventListener("scroll", () => {
    repositionAllO2PPathSelectors();
}, { passive: true });
window.addEventListener("resize", () => {
    repositionAllO2PPathSelectors();
}, { passive: true });

// The o2p path selectors follow the authoring context, not a gesture (a
// double-tap on the score is play / pause, see transport/transportTouch.js):
// dslInspector.js pins them visible for as long as the DSL editor is open,
// via setO2PSelectorsPinned().

// ============================================================
// Geometry helpers
// ============================================================
function localToScreen(el, x, y) {
    const svg = el.ownerSVGElement;
    if (!svg) return { x, y };

    const pt = svg.createSVGPoint();
    pt.x = x;
    pt.y = y;

    const m = el.getScreenCTM();
    if (!m) return { x, y };

    const res = pt.matrixTransform(m);
    const { dx, dy } = screenSpaceDelta(svg);
    return { x: res.x + dx, y: res.y + dy };
}

// ------------------------------------------------------------
// Choose largest child shape in group
// ------------------------------------------------------------
function chooseLargestShape(groupEl) {
    if (groupEl.tagName.toLowerCase() !== "g") return groupEl;

    let best = null;
    let bestArea = -1;

    groupEl.querySelectorAll(
        "circle, ellipse, rect, line, polyline, polygon, path"
    ).forEach(s => {
        const b = s.getBBox();
        const area = b.width * b.height;
        if (area > bestArea) {
            bestArea = area;
            best = s;
        }
    });

    return best || groupEl;
}

// ------------------------------------------------------------
// Anchors
// ------------------------------------------------------------
export function computeBBoxCenterScreen(groupEl) {
    const box = groupEl.getBoundingClientRect();
    return {
        x: box.left + box.width / 2,
        y: box.top + box.height / 2
    };
}

function computePathAnchorScreen(groupEl, t = 0) {
    const shape = chooseLargestShape(groupEl);
    if (!shape) return null;

    const tag = shape.tagName.toLowerCase();

    if (tag === "path") {
        try {
            const len = shape.getTotalLength();
            const p = shape.getPointAtLength(len * t);
            return localToScreen(shape, p.x, p.y);
        } catch {
            return null;
        }
    }

    const box = shape.getBBox();
    const w = box.width;
    const h = box.height;
    const perimeter = 2 * (w + h);
    const d = (t * perimeter) % perimeter;

    let x, y;
    if (d <= w) {
        x = box.x + d;
        y = box.y;
    } else if (d <= w + h) {
        x = box.x + w;
        y = box.y + (d - w);
    } else if (d <= 2 * w + h) {
        x = box.x + (w - (d - w - h));
        y = box.y + h;
    } else {
        x = box.x;
        y = box.y + (h - (d - 2 * w - h));
    }

    return localToScreen(shape, x, y);
}

// Internal: reposition ring overlay to follow fader's screen position
export function repositionRotationRing(hitRecord, ringData) {
    if (!hitRecord?.groupEl || !ringData) return;

    const pos = computeBBoxCenterScreen(hitRecord.groupEl);
    if (!pos) return;

    const half = parseFloat(ringData.container.style.width) / 2;

    ringData.container.style.left = `${pos.x - half}px`;
    ringData.container.style.top  = `${pos.y - half}px`;
    ringData.hit.style.left = `${pos.x}px`;
    ringData.hit.style.top  = `${pos.y}px`;
}

// ------------------------------------------------------------
// CREATE HIT LABEL
// ------------------------------------------------------------
// Hit labels sit at z 9000, below panels and dialogs -- and below an open
// page (#singlePage-container, z 25000), so an object ON a page could not be
// clicked, dragged or touched. Those get a layer above the page.
const PAGE_HIT_Z = 25001;
function hitLayerZ(groupEl) {
    return groupEl?.closest?.("#singlePage-container") ? PAGE_HIT_Z : 9000;
}

export function createHitLabel(groupEl, kind, uid, opts = {}) {
    if (!groupEl) return;
    const layerZ = hitLayerZ(groupEl);

    // Refresh existing hit labels on re-registration (e.g. mode switch)
    const existing = window._oscillaHitLabels?.find(r => r.uid === uid);
    if (existing) {
        // Update record to track current element and options
        existing.groupEl = groupEl;
        existing.kind = kind;
        existing.anchorMode = opts.anchorMode || existing.anchorMode;
        existing.color = opts.color || existing.color;
        existing.sizeMode = opts.sizeMode || existing.sizeMode;
        existing.isTouchMode = opts.isTouchMode || existing.isTouchMode;
        existing.oscToggleEnabled = opts.oscToggle || false;
        // Restore oscMode/oscColor from localStorage if available (legacy)
        const saved = opts.oscToggle ? loadOscState(uid) : null;
        existing.oscMode = saved?.oscMode || false;
        existing.oscColor = saved?.oscColor || null;
        // toggleStates system
        existing.toggleStates = opts.toggleStates || null;
        existing.toggleColors = opts.toggleColors || null;
        existing.onToggle = opts.onToggle || null;
        if (opts.toggleStates) {
            const savedToggle = loadToggleState(uid);
            existing.toggleStateIndex = savedToggle?.stateIndex ?? 0;
            existing.toggleColor = savedToggle?.color || null;
        }
        for (const d of [existing.hit, existing.div, existing.valueLabel]) if (d) d.style.zIndex = layerZ;
        updateHitCircle(existing);
        return existing;
    }

    // Restore saved OSC state if available (legacy)
    const savedOsc = opts.oscToggle ? loadOscState(uid) : null;
    // Restore saved toggle state if available
    const savedToggle = opts.toggleStates ? loadToggleState(uid) : null;

    const record = {
        groupEl,
        uid,
        kind,
        anchorMode: opts.anchorMode || "pathStart",
        color: opts.color || "red",
        sizeMode: opts.sizeMode || "fixed",
        // Legacy OSC toggle
        oscMode: savedOsc?.oscMode || false,  // restore from localStorage or default false
        oscColor: savedOsc?.oscColor || null,  // restore saved client color
        oscToggleEnabled: opts.oscToggle || false,  // only allow toggle if explicitly enabled
        // toggleStates system (decoupled from OSC)
        toggleStates: opts.toggleStates || null,  // array of state names e.g. ["idle", "claimed"]
        toggleColors: opts.toggleColors || null,  // array of colors per state e.g. ["none", "#client"]
        onToggle: opts.onToggle || null,  // cue expression to run on toggle
        toggleStateIndex: savedToggle?.stateIndex ?? 0,  // current state index
        toggleColor: savedToggle?.color || null,  // current color (client who toggled)
        toggledByLocal: false,  // track if local client owns this toggle
        div: null,  // visible dot
        hit: null,  // invisible hit area
        valueLabel: null,  // value display for touch mode
        isTouchMode: opts.isTouchMode || false
    };

    // -----------------------------
    // HIT AREA - size depends on mode
    // -----------------------------
    const hit = document.createElement("div");
    hit.dataset.uid = uid;

    // For touch mode, start with smaller hit area that will be updated to match circle
    // Non-touch: slightly larger than the 14px visible dot for easier clicking
    const hitSize = opts.isTouchMode ? 40 : 28;
    const hitOffset = hitSize / 2;

    Object.assign(hit.style, {
        position: "fixed",
        left: "0px",
        top: "0px",
        width: `${hitSize}px`,
        height: `${hitSize}px`,
        marginLeft: `-${hitOffset}px`,
        marginTop: `-${hitOffset}px`,
        background: "transparent",
        pointerEvents: "auto",
        zIndex: layerZ,  // Below topbar (99999), panels (10100), dialogs (10200)
        borderRadius: opts.isTouchMode ? "50%" : "4px",  // Circular for touch, rect for buttons
        willChange: "transform, left, top",  // GPU hint
        contain: "layout style"  // Reduce repaint scope
    });

    hit.classList.add("oscilla-hit");
    hit.dataset.oscillaHit = "1";

    // Track click timing for single vs double click detection
    let lastClickTime = 0;
    let clickTimeout = null;
    const DOUBLE_CLICK_THRESHOLD = 300; // ms

    hit.addEventListener("click", e => {
        e.stopPropagation();
        
        const now = Date.now();
        const timeSinceLastClick = now - lastClickTime;
        lastClickTime = now;

        // Clear any pending single-click dispatch
        if (clickTimeout) {
            clearTimeout(clickTimeout);
            clickTimeout = null;
        }

        if (timeSinceLastClick < DOUBLE_CLICK_THRESHOLD) {
            // DOUBLE CLICK → cycle toggle states or toggle OSC mode

            // toggleStates system (decoupled from OSC)
            if (record.toggleStates && record.toggleStates.length > 0) {
                const numStates = record.toggleStates.length;
                const prevIndex = record.toggleStateIndex || 0;
                const nextIndex = (prevIndex + 1) % numStates;

                record.toggleStateIndex = nextIndex;
                const stateName = record.toggleStates[nextIndex];

                // Determine color for this state
                const clientColor = window.localClientColor || localStorage.getItem("clientColor") || "#ffaa00";
                if (record.toggleColors && record.toggleColors[nextIndex]) {
                    const colorSpec = record.toggleColors[nextIndex];
                    if (colorSpec === "#client" || colorSpec === "client") {
                        record.toggleColor = clientColor;
                    } else if (colorSpec === "none" || colorSpec === "null" || !colorSpec) {
                        record.toggleColor = null;
                    } else {
                        record.toggleColor = colorSpec;
                    }
                } else {
                    // Default: state 0 has no color, others use client color
                    record.toggleColor = nextIndex > 0 ? clientColor : null;
                }

                record.toggledByLocal = nextIndex > 0;

                // Persist to localStorage
                saveToggleState(uid, nextIndex, record.toggleColor);

                // Update visual color
                updateHitCircleColor(record);

                // Dispatch toggle event with state info
                groupEl.dispatchEvent(
                    new CustomEvent("oscilla-toggle-state", {
                        bubbles: true,
                        detail: {
                            kind,
                            uid,
                            stateIndex: nextIndex,
                            stateName,
                            prevStateIndex: prevIndex,
                            prevStateName: record.toggleStates[prevIndex],
                            color: record.toggleColor,
                            onToggle: record.onToggle  // cue expression to execute
                        }
                    })
                );
                return;
            }

            // LEGACY: oscToggle system (gates OSC sending)
            if (!record.oscToggleEnabled) {
                // Neither toggleStates nor oscToggle enabled - ignore double-click
                return;
            }

            record.oscMode = !record.oscMode;
            // Set color to local client's color when toggling on
            if (record.oscMode) {
                record.oscColor = window.localClientColor || localStorage.getItem("clientColor") || "#ffaa00";
                record.toggledByLocal = true;  // Track that local client toggled this
            } else {
                record.toggledByLocal = false;
            }
            // Persist to localStorage so it survives page reload
            saveOscState(uid, record.oscMode, record.oscColor);

            // Update visual color
            updateHitCircleColor(record);

            // Include UID in event so only matching animation responds
            groupEl.dispatchEvent(
                new CustomEvent("oscilla-osc-toggle", {
                    bubbles: true,
                    detail: {
                        kind,
                        uid,
                        oscEnabled: record.oscMode
                    }
                })
            );

        } else {
            // Wait to see if it's a double click
            clickTimeout = setTimeout(() => {
                // allow:/lock: on the motion's cue (controlAccess.js)
                if (!mayOperate(window.oscillaAnimRegistry?.[uid]?.el ?? groupEl)) return;
                // SINGLE CLICK → play/pause/resume (OSC state unchanged)
                groupEl.dispatchEvent(
                    new CustomEvent("oscilla-hit", {
                        bubbles: true,
                        detail: { kind, uid }  // ← Also include UID for consistency
                    })
                );

            }, DOUBLE_CLICK_THRESHOLD);
        }
    });

    // -----------------------------
    // VISIBLE DOT
    // -----------------------------
    const div = document.createElement("div");
    div.dataset.uid = uid;

    Object.assign(div.style, {
        position: "fixed",
        left: "0px",
        top: "0px",
        width: "14px",
        height: "14px",
        borderRadius: "50%",
        background: record.sizeMode === "fixed" ? record.color : "transparent",
        border: record.sizeMode === "follow"
            ? `0.75px solid ${record.color}`
            : "none",
        zIndex: layerZ,  // Below topbar (99999), panels (10100), dialogs (10200)
        pointerEvents: "none",
        boxSizing: "border-box",
        willChange: "transform, left, top",  // GPU hint
        contain: "layout style"  // Reduce repaint scope
    });

    // -----------------------------
    // VALUE LABEL (for touch mode)
    // -----------------------------
    let valueLabel = null;
    if (opts.isTouchMode) {
        // Check if value label already exists in DOM
        const existingLabel = document.querySelector(`div[data-value-label="1"][data-uid="${uid}"]`);
        if (existingLabel) {
            valueLabel = existingLabel;
        } else {
            valueLabel = document.createElement("div");
            valueLabel.dataset.uid = uid;
            valueLabel.dataset.valueLabel = "1";
            
            Object.assign(valueLabel.style, {
                position: "fixed",
                left: "0px",
                top: "0px",
                transform: "translate(-50%, -150%)",  // Position above the circle
                color: "black",
                backgroundColor: "white",
                padding: "1px 3px",
                fontSize: "9px",
                fontFamily: "monospace",
                fontWeight: "bold",
                zIndex: layerZ,  // Below topbar (99999), panels (10100), dialogs (10200)
                pointerEvents: "none",
                whiteSpace: "nowrap",
                willChange: "left, top",  // GPU hint
                contain: "layout style"  // Reduce repaint scope
            });
            valueLabel.textContent = "0.00";
            
            document.body.appendChild(valueLabel);
        }
        record.valueLabel = valueLabel;
    }

    document.body.appendChild(hit);
    document.body.appendChild(div);

    record.div = div;
    record.hit = hit;

    window._oscillaHitLabels.push(record);

    // Polygon selectors are made here, the moment a rotate hit exists:
    // rotate hits arrive at arbitrary times during slow boots (WebSocket
    // state replay, chunked SVG load), and the overlay gate flips on with
    // the first one.
    if (uid && uid.startsWith("rot_") && !window._o2pPathSelectors[uid]) {
        createO2PPathSelector(uid, div);
        window.dispatchEvent(new CustomEvent("oscilla:o2p-selectors-ready", {
            detail: { count: Object.keys(window._o2pPathSelectors).length }
        }));
    }

    updateHitCircle(record);
    _ensureHitLabelLoop();
}

// ------------------------------------------------------------
// UPDATE ONE HIT CIRCLE
// ------------------------------------------------------------
export function updateHitCircle(rec) {
    const { groupEl, div, hit, anchorMode, sizeMode, color, oscMode } = rec;
    if (!groupEl || !div || !hit) return;

    // Respect explicit display:none set by setElementVisibility — without
    // this guard the rotate-mode branch below resets display to "flex" each
    // tick, undoing the polygon's visibility toggle 100ms later.
    if (div.style.display === "none") return;

    // Group-based visibility: hide overlays for inactive groups
    if (!shouldShowOverlayByGroup(rec)) {
        div.style.opacity = "0";
        hit.style.pointerEvents = "none";
        if (rec.valueLabel) rec.valueLabel.style.opacity = "0";
        return;  // Skip further updates for hidden overlay
    }
    // Ensure value label visible when group is active
    if (rec.valueLabel) rec.valueLabel.style.opacity = "1";

    // Opacity + pointer-event sync
    // In SCROLL mode: hide handle until animation element is active
    // In PAGE mode:   always show handle (original behaviour)
    try {
        const cfg = groupEl._oscillaCfg;
        const gs = cfg?._armState;
        const isScrollMode = window.mode === "scroll";

        if (isScrollMode && cfg?._armed) {
            // armed scroll: hide while still "registered" (pre-arm)
            const active = (gs === "armed" || gs === "running" || gs === "paused");
            div.style.opacity = active ? "1" : "0";
            hit.style.pointerEvents = active ? "auto" : "none";
        } else if (isScrollMode && cfg?._fadeIn) {
            // fadein scroll: hide until fade has been triggered
            const active = !!cfg._fadeInTriggered;
            div.style.opacity = active ? "1" : "0";
            hit.style.pointerEvents = active ? "auto" : "none";
        } else if (cfg?._armed) {
            // armed page: always show
            div.style.opacity = "1";
            hit.style.pointerEvents = "auto";
        } else {
            // default: assume visible (avoid expensive getComputedStyle)
            // Only check inline display/visibility for hidden elements
            const isHidden = groupEl.style.display === "none" ||
                             groupEl.style.visibility === "hidden" ||
                             groupEl.style.opacity === "0";
            div.style.opacity = isHidden ? "0" : "1";
            hit.style.pointerEvents = isHidden ? "none" : "auto";
        }
    } catch {
        div.style.opacity = "1";
        hit.style.pointerEvents = "auto";
    }

    // Anchor
    let pos = null;
    switch (anchorMode) {
        case "object":
            pos = computeBBoxCenterScreen(groupEl);
            break;
        case "pathMidPoint":
            pos = computePathAnchorScreen(groupEl, 0.5);
            break;
        case "center":
            pos = computeBBoxCenterScreen(groupEl);
            break;
        case "pathStart":
        default:
            pos = computePathAnchorScreen(groupEl, 0);
    }

    if (!pos) return;
    
    // --------------------------------------------------
    // Size modes - respecting oscMode for double ring
    // --------------------------------------------------
    const RING_SIZE = 40;
    const RING_BORDER = 0.75;
    // Use stored client color, fallback to amber
    const OSC_COLOR = rec.oscColor || "#ffaa00";

    if (sizeMode === "follow") {
        const box = groupEl.getBoundingClientRect();
        const r = Math.max(box.width, box.height) / 2 + 6;
        const size = r * 2;

        div.style.width = `${size}px`;
        div.style.height = `${size}px`;

        // Check for toggle state claiming (show fill when claimed)
        const hasToggleStateColor = rec.toggleStates && rec.toggleStateIndex > 0 && rec.toggleColor;
        if (hasToggleStateColor) {
            // Claimed: fill with client color, border becomes client color
            div.style.background = `${rec.toggleColor}66`;  // 40% opacity fill
            div.style.border = `3px solid ${rec.toggleColor}`;
            div.style.boxShadow = `0 0 8px ${rec.toggleColor}`;
        } else if (oscMode) {
            div.style.background = "transparent";
            div.style.border = `2px solid ${color}`;
            div.style.boxShadow = `0 0 0 4px ${OSC_COLOR}, 0 0 12px ${OSC_COLOR}`;
        } else {
            div.style.background = "transparent";
            div.style.border = "none";
            div.style.boxShadow = "none";
        }

        div.style.left = `${pos.x - size / 2}px`;
        div.style.top = `${pos.y - size / 2}px`;

        // Match hit area to visible circle for all follow mode elements
        hit.style.width = `${size}px`;
        hit.style.height = `${size}px`;
        hit.style.marginLeft = `-${size / 2}px`;
        hit.style.marginTop = `-${size / 2}px`;
        hit.style.borderRadius = rec.isTouchMode ? "50%" : "4px";  // Rect for buttons
    }
    else if (sizeMode === "rotate40") {
        // Rotate overlay: discrete red ring with "R" label and large invisible hit area
        const ROTATE_DOT_SIZE = 18;
        const ROTATE_HIT_SIZE = 88;
        const ringColor = "#cc3333";

        div.style.width = `${ROTATE_DOT_SIZE}px`;
        div.style.height = `${ROTATE_DOT_SIZE}px`;
        div.style.display = "flex";
        div.style.alignItems = "center";
        div.style.justifyContent = "center";
        div.style.fontSize = "10px";
        div.style.fontWeight = "normal";
        div.style.fontFamily = "monospace";
        div.style.background = "transparent";
        div.style.border = `1px solid ${ringColor}`;
        div.style.boxShadow = "none";
        div.style.color = ringColor;

        // Add "R" label if not already present
        if (!div.textContent || div.textContent !== "R") {
            div.textContent = "R";
        }

        div.style.left = `${pos.x - ROTATE_DOT_SIZE / 2}px`;
        div.style.top = `${pos.y - ROTATE_DOT_SIZE / 2}px`;

        // Make hit area larger for touch
        hit.style.width = `${ROTATE_HIT_SIZE}px`;
        hit.style.height = `${ROTATE_HIT_SIZE}px`;
        hit.style.marginLeft = `-${ROTATE_HIT_SIZE / 2}px`;
        hit.style.marginTop = `-${ROTATE_HIT_SIZE / 2}px`;
        hit.style.borderRadius = "50%";
    }
    else if (
        sizeMode === "ring40" ||
        sizeMode === "scale40"
    ) {
        const size = RING_SIZE;

        let ringColor = color;
        if (sizeMode === "scale40") ringColor = "#33ccff";

        div.style.width = `${size}px`;
        div.style.height = `${size}px`;

        // Check for toggle state claiming (show fill when claimed)
        const hasToggleStateColor = rec.toggleStates && rec.toggleStateIndex > 0 && rec.toggleColor;
        if (hasToggleStateColor) {
            // Claimed: fill with client color, border becomes client color
            div.style.background = `${rec.toggleColor}66`;  // 40% opacity fill
            div.style.border = `3px solid ${rec.toggleColor}`;
            div.style.boxShadow = `0 0 8px ${rec.toggleColor}`;
        } else if (oscMode) {
            div.style.background = "transparent";
            div.style.border = `2px solid ${ringColor}`;
            div.style.boxShadow = `0 0 0 4px ${OSC_COLOR}, 0 0 12px ${OSC_COLOR}`;
        } else {
            div.style.background = "transparent";
            div.style.border = `${RING_BORDER}px solid ${ringColor}`;
            div.style.boxShadow = "none";
        }

        div.style.left = `${pos.x - size / 2}px`;
        div.style.top = `${pos.y - size / 2}px`;
    }
    else {
        div.style.width = "14px";
        div.style.height = "14px";
        div.style.border = "none";

        if (oscMode) {
            div.style.background = color;
            div.style.boxShadow = `0 0 0 3px ${OSC_COLOR}, 0 0 10px ${OSC_COLOR}`;
        } else {
            div.style.background = color;
            div.style.boxShadow = "none";
        }

        div.style.left = `${pos.x - 7}px`;
        div.style.top = `${pos.y - 7}px`;
    }

    hit.style.left = `${pos.x}px`;
    hit.style.top = `${pos.y}px`;
    
    // Update value label position if it exists
    if (rec.valueLabel) {
        rec.valueLabel.style.left = `${pos.x}px`;
        rec.valueLabel.style.top = `${pos.y}px`;
    }

    // Reposition rotation ring if attached
    if (rec._rotationRing) {
        repositionRotationRing(rec, rec._rotationRing);
    }
}

// ------------------------------------------------------------
// UPDATE ALL HIT LABELS (expensive - use sparingly)
// ------------------------------------------------------------
export function repositionAllHitLabels() {
    for (const rec of window._oscillaHitLabels) {
        updateHitCircle(rec);
    }
    // Also reposition selectors
    repositionAllO2PPathSelectors();
}

// ------------------------------------------------------------
// FAST POSITION-ONLY UPDATE FOR SINGLE LABEL (use in animation ticks)
// Only updates position, skips visibility/size checks
// ------------------------------------------------------------
export function repositionHitLabelFast(uid) {
    const rec = window._oscillaHitLabels?.find(r => r.uid === uid);
    if (!rec || !rec.groupEl || !rec.div) return;

    const { groupEl, div, hit, anchorMode, sizeMode, valueLabel } = rec;

    // Fast position calculation - just get center
    let pos;
    if (anchorMode === "pathStart") {
        pos = computePathAnchorScreen(groupEl, 0);
    } else if (anchorMode === "pathMidPoint") {
        pos = computePathAnchorScreen(groupEl, 0.5);
    } else {
        // Default: bbox center (most common for rotate/scale)
        const box = groupEl.getBoundingClientRect();
        pos = { x: box.left + box.width / 2, y: box.top + box.height / 2 };
    }

    if (!pos) return;

    // Get current div size for centering offset
    // Use cached size or parse from style (avoids layout thrash)
    let size;
    if (sizeMode === "follow") {
        // For follow mode, size changes - read current width
        size = parseFloat(div.style.width) || 14;
    } else if (sizeMode === "rotate40") {
        size = 18;  // Smaller rotate overlay
    } else if (sizeMode === "ring40" || sizeMode === "scale40") {
        size = 40;
    } else {
        size = 14;
    }

    // Update position with centering offset
    div.style.left = `${pos.x - size / 2}px`;
    div.style.top = `${pos.y - size / 2}px`;

    // Update hit area position
    if (hit) {
        hit.style.left = `${pos.x}px`;
        hit.style.top = `${pos.y}px`;
    }

    // Update value label position
    if (valueLabel) {
        valueLabel.style.left = `${pos.x}px`;
        valueLabel.style.top = `${pos.y}px`;
    }
}

// ------------------------------------------------------------
// FAST UPDATE FOR LABELS THAT ARE CHILDREN OF AN ELEMENT
// Use when parent transforms (rotate/scale) affect child positions
// ------------------------------------------------------------
export function repositionChildHitLabels(parentEl) {
    if (!parentEl || !window._oscillaHitLabels) return;

    for (const rec of window._oscillaHitLabels) {
        // Skip if this label's element is the parent itself
        if (rec.groupEl === parentEl) continue;

        // Check if this label's element is a descendant of parentEl
        if (parentEl.contains(rec.groupEl)) {
            repositionHitLabelFast(rec.uid);
        }
    }
}

// ------------------------------------------------------------
// UPDATE VALUE LABEL (for touch mode sliders)
// ------------------------------------------------------------
export function updateHitLabelValue(uid, value) {
    const rec = window._oscillaHitLabels?.find(r => r.uid === uid);
    if (!rec || !rec.valueLabel) return;
    
    // Format to 2 decimal places
    rec.valueLabel.textContent = value.toFixed(2);
}

// ------------------------------------------------------------
// UPDATE HIT CIRCLE COLOR (for OSC mode toggle)
// ------------------------------------------------------------
export function updateHitCircleColor(rec) {
    const { div, color, sizeMode, oscMode, oscColor, toggleStates, toggleStateIndex, toggleColor } = rec;
    if (!div) return;

    // Determine if we have an active state with color
    // toggleStates: active if stateIndex > 0 and toggleColor is set
    // Legacy oscMode: active if oscMode is true
    const hasToggleStateColor = toggleStates && toggleStateIndex > 0 && toggleColor;
    const isActive = hasToggleStateColor || oscMode;
    const activeColor = toggleColor || oscColor || "#ffaa00";

    if (sizeMode === "follow") {
        if (isActive) {
            div.style.border = `2px solid ${color}`;
            div.style.boxShadow = `0 0 0 4px ${activeColor}, 0 0 12px ${activeColor}`;
        } else {
            div.style.border = "none";
            div.style.boxShadow = "none";
        }
    }
    else if (sizeMode === "rotate40") {
        // Simple styling - no claiming logic needed
        const ringColor = "#cc3333";
        div.style.background = "transparent";
        div.style.border = `1px solid ${ringColor}`;
        div.style.boxShadow = "none";
        div.style.color = ringColor;
    }
    else if (
        sizeMode === "ring40" ||
        sizeMode === "scale40"
    ) {
        let ringColor = color;
        if (sizeMode === "scale40") ringColor = "#33ccff";

        if (hasToggleStateColor) {
            // Claimed: fill with client color, border becomes client color
            div.style.background = `${activeColor}66`;  // 40% opacity fill
            div.style.border = `3px solid ${activeColor}`;
            div.style.boxShadow = `0 0 8px ${activeColor}`;
        } else if (oscMode) {
            div.style.background = "transparent";
            div.style.border = `2px solid ${ringColor}`;
            div.style.boxShadow = `0 0 0 4px ${activeColor}, 0 0 12px ${activeColor}`;
        } else {
            div.style.background = "transparent";
            div.style.border = `0.75px solid ${ringColor}`;
            div.style.boxShadow = "none";
        }
    }
    else {
        if (isActive) {
            div.style.background = color;
            div.style.boxShadow = `0 0 0 3px ${activeColor}, 0 0 10px ${activeColor}`;
        } else {
            div.style.background = color;
            div.style.boxShadow = "none";
        }
    }
}

// ------------------------------------------------------------
// DESTROY ALL HIT LABELS
// ------------------------------------------------------------
export function destroyAllHitLabels() {
    document
        .querySelectorAll("[data-oscilla-hit], .oscilla-hit")
        .forEach(el => {
            el.remove();
        });

    // Remove rotation ring overlays
    document
        .querySelectorAll(".oscilla-rotation-ring, .oscilla-rotation-ring-hit")
        .forEach(el => el.remove());

    if (window._oscillaHitLabels) {
        for (const rec of window._oscillaHitLabels) {
            rec.div?.remove();
            rec.hit?.remove();
            rec.valueLabel?.remove();       // a touch fader's "0.50"
            // Clean up rotation ring if attached
            if (rec._rotationRing) {
                rec._rotationRing.container?.remove();
                rec._rotationRing.hit?.remove();
                delete rec._rotationRing;
            }
        }
        window._oscillaHitLabels.length = 0;
    }

    _stopHitLabelLoop();

    // Clean up rotation drag sessions
    for (const [key] of window._oscillaDragSessions) {
        if (key.startsWith("rot:")) {
            window._oscillaDragSessions.delete(key);
        }
    }
}


/**
 * A project load: the polygon selectors (their dots and hit areas in the
 * body), the selected groups and the touch-fader groups belong to the score
 * being left. Emptied in place, as extensions hold on to these objects.
 * Left behind, a selector made every later score's grouped o2p overlays
 * wait for a selection (shouldShowOverlayByGroup), and a group kept
 * _launcherCreated, so coming back built no launcher.
 */
export function resetO2PGroups() {
    const selectors = window._o2pPathSelectors;
    for (const uid of Object.keys(selectors)) {
        selectors[uid].dot?.remove();
        selectors[uid].hit?.remove();
        delete selectors[uid];
    }
    window._activeO2PGroups.clear();
    const groups = window._o2pTouchGroups || {};
    for (const id of Object.keys(groups)) delete groups[id];
}
// the o2p touch overlays, polygon selectors, selected and touch groups
onReset("project", destroyAllHitLabels);
onReset("project", resetO2PGroups);

/**
 * Remove one uid's hit label, with its listeners. A live re-run that
 * changes an o2p's mode (a moving object becoming a touch fader) builds a
 * fresh one: the old one's drag handler holds the old cfg.
 */
export function destroyHitLabel(uid) {
    const list = window._oscillaHitLabels;
    if (!list) return;
    for (let i = list.length - 1; i >= 0; i--) {
        const rec = list[i];
        if (rec.uid !== uid) continue;
        rec.div?.remove();
        rec.hit?.remove();
        rec.valueLabel?.remove();
        if (rec._rotationRing) {
            rec._rotationRing.container?.remove();
            rec._rotationRing.hit?.remove();
        }
        list.splice(i, 1);
    }
}
